import { supabase } from '@/lib/supabase';
import type { AppNotification } from '@/types';
import type { RealtimeChannel } from '@supabase/supabase-js';

export async function fetchNotifications(
  userId: string,
  limit = 20,
  offset = 0
): Promise<{ data: AppNotification[]; count: number }> {
  const { data, error, count } = await supabase
    .from('notifications')
    .select('*', { count: 'exact' })
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return { data: (data as AppNotification[]) || [], count: count || 0 };
}

export async function fetchUnreadCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) throw error;
  return count || 0;
}

export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);

  if (error) throw error;
}

export async function markAllAsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) throw error;
}

export async function deleteNotification(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('id', notificationId);

  if (error) throw error;
}

interface NotificationRealtimeHandlers {
  onInsert: (notification: AppNotification) => void;
  onUpdate: (notification: AppNotification) => void;
  onDelete: (notificationId: string) => void;
}

export function subscribeToNotifications(
  userId: string,
  handlers: NotificationRealtimeHandlers
): RealtimeChannel {
  const channel = supabase
    .channel(`notifications:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        handlers.onInsert(payload.new as AppNotification);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        handlers.onUpdate(payload.new as AppNotification);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        handlers.onDelete((payload.old as AppNotification).id);
      }
    )
    .subscribe();

  return channel;
}
